import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  useRetmBranchMappings,
  useRetmBranches,
  useRetmCreateBranchMapping,
  useRetmDeleteBranchMapping,
} from "src/api/integrations/retm";
import type { RetmBranchMapping } from "src/types/retm";
import { useGetBranches } from "src/api/generic";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";

const RetmMappings = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] =
    useState<RetmBranchMapping | null>(null);
  const [selectedJazrBranch, setSelectedJazrBranch] = useState<number | "">("");
  const [selectedRetmBranch, setSelectedRetmBranch] = useState<string>("");

  // API Hooks
  const { data: mappingsData, isLoading: mappingsLoading, refetch: refetchMappings } = useRetmBranchMappings();
  const { data: retmBranchesData, isLoading: retmBranchesLoading, refetch: refetchRetmBranches } = useRetmBranches();
  const { data: jazrBranchesData, isLoading: jazrBranchesLoading } = useGetBranches();
  const { mutate: createMapping, status: createStatus, reset: resetCreate } = useRetmCreateBranchMapping();
  const { mutate: deleteMapping, status: deleteStatus, reset: resetDelete } = useRetmDeleteBranchMapping();

  const mappings = mappingsData?.data || [];
  const retmBranches = retmBranchesData?.data || [];
  const jazrBranches = jazrBranchesData?.data || [];
  const currentLang = i18n.language || "en";

  // Helper to get branch name from {en, ar} object or string
  const getBranchName = (name: string | { en: string; ar: string } | undefined): string => {
    if (!name) return "-";
    if (typeof name === "string") return name;
    return name[currentLang as keyof typeof name] || name.en || "-";
  };
  const emptyDataArr = mappings.length === 0;

  // Get unmapped Jazr branches (not already mapped)
  const mappedJazrBranchIds = mappings.map((m) => m.internal_id);
  const unmappedJazrBranches = jazrBranches.filter((b) => !mappedJazrBranchIds.includes(b.id));

  // Get unmapped Retm branches
  const mappedRetmBranchIds = mappings.map((m) => m.external_id);
  const unmappedRetmBranches = retmBranches.filter((b) => !mappedRetmBranchIds.includes(b.uuid));

  const handleAddMapping = () => {
    if (!selectedJazrBranch || !selectedRetmBranch) return;

    const retmBranch = retmBranches.find((b) => b.uuid === selectedRetmBranch);

    createMapping({
      internal_id: selectedJazrBranch as number,
      external_id: selectedRetmBranch,
      external_code: retmBranch?.code,
      external_name: getBranchName(retmBranch?.name),
    });
  };

  const handleDeleteMapping = () => {
    if (!selectedMapping) return;
    deleteMapping(selectedMapping.id);
  };

  const handleOpenDeleteDialog = (mapping: RetmBranchMapping) => {
    setSelectedMapping(mapping);
    setDeleteDialogOpen(true);
  };

  const resetAddForm = () => {
    setSelectedJazrBranch("");
    setSelectedRetmBranch("");
    setAddDialogOpen(false);
    resetCreate();
  };

  // Handle create success/error
  useEffect(() => {
    if (createStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("mappings.saveSuccess"),
        },
      ]);
      resetAddForm();
      refetchMappings();
    } else if (createStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("mappings.saveError"),
        },
      ]);
    }
  }, [createStatus]);

  // Handle delete success/error
  useEffect(() => {
    if (deleteStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("mappings.deleteSuccess"),
        },
      ]);
      setDeleteDialogOpen(false);
      setSelectedMapping(null);
      resetDelete();
      refetchMappings();
    } else if (deleteStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("mappings.deleteError"),
        },
      ]);
    }
  }, [deleteStatus]);

  const tableHeadText = [
    t("mappings.table.jazrBranch"),
    t("mappings.table.retmBranch"),
    t("mappings.table.externalId"),
    t("mappings.table.lastSynced"),
    t("mappings.table.actions"),
  ];

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/integrations/retm")}
            sx={{ color: "#667085" }}
          >
            {generalT("back")}
          </Button>
          <Box>
            <Typography fontSize={24} fontWeight={600} color="#101828">
              {t("mappings.title")}
            </Typography>
            <Typography fontSize={14} color="#667085">
              {t("mappings.subtitle")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={retmBranchesLoading ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={() => refetchRetmBranches()}
            disabled={retmBranchesLoading}
          >
            {t("mappings.fetchBranches")}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={unmappedJazrBranches.length === 0 || unmappedRetmBranches.length === 0}
          >
            {t("mappings.addMapping")}
          </Button>
        </Stack>
      </Box>

      {/* Divider */}
      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {/* Table */}
      <Box>
        <TableContainer component={Paper} variant="outlined">
          <Table sx={{ minWidth: 650 }} aria-label="mappings table">
            <TableHead>
              <TableRow>
                {tableHeadText.map((item) => (
                  <TableCell
                    key={item}
                    align="center"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      color: "#475467",
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {mappingsLoading ? (
              <TableLoadingSkeleton rowsLength={5} cellsLength={5} />
            ) : (
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow
                    key={mapping.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell align="center">{getBranchName(mapping.internal_name) || mapping.internal_id}</TableCell>
                    <TableCell align="center">{getBranchName(mapping.external_name) || "-"}</TableCell>
                    <TableCell align="center">{mapping.external_id}</TableCell>
                    <TableCell align="center">
                      {mapping.synced_at
                        ? new Date(mapping.synced_at).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(mapping)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {!mappingsLoading && emptyDataArr && <NoData />}
        </TableContainer>
      </Box>

      {/* Add Mapping Dialog */}
      <Dialog open={addDialogOpen} onClose={resetAddForm} maxWidth="sm" fullWidth>
        <DialogTitle>{t("mappings.addMapping")}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <FormControl fullWidth>
              <InputLabel>{t("mappings.form.jazrBranch")}</InputLabel>
              <Select
                value={selectedJazrBranch}
                label={t("mappings.form.jazrBranch")}
                onChange={(e) => setSelectedJazrBranch(e.target.value as number)}
                disabled={jazrBranchesLoading}
              >
                <MenuItem value="" disabled>
                  {t("mappings.form.selectJazrBranch")}
                </MenuItem>
                {unmappedJazrBranches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {getBranchName(branch.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t("mappings.form.retmBranch")}</InputLabel>
              <Select
                value={selectedRetmBranch}
                label={t("mappings.form.retmBranch")}
                onChange={(e) => setSelectedRetmBranch(e.target.value as string)}
                disabled={retmBranchesLoading}
              >
                <MenuItem value="" disabled>
                  {t("mappings.form.selectRetmBranch")}
                </MenuItem>
                {unmappedRetmBranches.map((branch) => (
                  <MenuItem key={branch.uuid} value={branch.uuid}>
                    {getBranchName(branch.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAddForm}>{t("mappings.form.cancel")}</Button>
          <Button
            onClick={handleAddMapping}
            variant="contained"
            disabled={!selectedJazrBranch || !selectedRetmBranch || createStatus === "pending"}
          >
            {createStatus === "pending" ? (
              <CircularProgress size={20} />
            ) : (
              t("mappings.form.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmPopup
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        handleConfirm={handleDeleteMapping}
        isPending={deleteStatus === "pending"}
        title={t("mappings.deleteMapping")}
        subtitle={t("mappings.deleteConfirm")}
        confirmBtnText={generalT("delete")}
      />
    </Stack>
  );
};

export default RetmMappings;
